import { BogeyState } from "../../enum/BogeyState";
import { SPGameState } from "../../enum/GameState";
import { Time } from "../../lib/Time";
import { Physics } from "../../math/Physics";
import Vector2 from "../../math/Vector2";
import { clamp01, lerp } from "../../util";
import { Random } from "../../util/Random";
import { Shapes } from "../../util/Shapes";
import { GravitonPulseBehavior } from "../SPAbilities/GravitonPulse";
import SPBogey from "../SPBogey";
import SPBullet from "../SPBullet";
import SPGameManager from "../SPGameManager";
import SPGun from "../SPGun";
import SPPlayer from "../SPPlayer";
import { HiveEgg, HivePart } from "./Hive";

enum State {
    AvoidingTarget,
    HeadingToLocation,
    DecidingLocation,
    ChoosingTarget,
    Wander
}

export default class SmartBogey extends SPBogey {
    _state: State = State.DecidingLocation;
    hp: number = 250;
    maxHP: number = 250;
    fireRate: number = 4;
    xpReward: number = 15;
    accelerationForce: number = 128;
    isFriendly: boolean = false;

    target: SPBogey|SPPlayer|undefined;

    targetPosition: Vector2|undefined;
    minPlayerDist = 128;
    maxPlayerDist = 512;
    targetStickRange = 256;
    maxShootRange = 384;

    gun: SPGun = new SPGun(this, {
        bulletSpeed: 200,
        damage: 1,
        fireRate: this.fireRate,
        magSize: 30,
        reloadTime: 2,
        maxEnemiesHit: 1
    });

    get distToTarget() {
        if (!this.target) return 0;
        return Vector2.distance(this.target.transform.position, this.transform.position);
    }

    SetState(state: State) {
        switch (state) {
            case State.AvoidingTarget:
                
                break;
            case State.DecidingLocation:
                
                break;
            case State.HeadingToLocation:
                
                break;
            case State.Wander:
                
                break;
        
            default:
                break;
        }

        this._state = state;
    }

    OverlapsTarget(range: number) {
        if (!this.target) return false;

        return Physics.CircleCircleCollision({
            position: this.transform.position,
            radius: range
        }, {
            position: this.target.transform.position,
            radius: this.target.size
        }) !== false;
    }

    AvoidBullets() {
        for (const bullet of this.engine.GetGameObjectsBehaviors<SPBullet>(SPBullet).filter(
            v => this.isFriendly ? !v.isPlayerBullet : v.isPlayerBullet
        )) {
            const line: Shapes.Line = {
                start: bullet.transform.position,
                end: bullet.transform.position.add(bullet.velocity.normalized.scale(128))
            };

            if (Physics.CircleLineCollision({
                position: this.transform.position,
                radius: this.size
            }, line)) {
                const dodgeVector = bullet.velocity.normalized.rotate(Math.PI/2);
                this.acceleration = dodgeVector.scale(this.accelerationForce * 2);
            }
        }
    }

    AvoidingTargetUpdate() {
        if (!this.target) return;

        if (this.distToTarget >= this.maxPlayerDist || this.isFriendly) {
            this.SetState(State.DecidingLocation);
            return;
        }

        const vecFromTarget = this.transform.position.sub(this.target.transform.position).normalized;
        //randomize it a bit
        const r = Random.float(-15, 15) * Math.PI / 180;
        vecFromTarget.rotate(r);

        this.acceleration = vecFromTarget.scale(this.accelerationForce * 2);
    }

    HeadingToLocationUpdate() {

    }

    DecidingLocationUpdate() {
        if (!this.target) return;
        
        if (this.distToTarget <= this.minPlayerDist && !this.isFriendly) {
            this.SetState(State.AvoidingTarget);
            return;
        }

        const vecFromTarget = this.transform.position.sub(this.target.transform.position).normalized;
        this.targetPosition = this.target.transform.position.add(vecFromTarget.scale(this.targetStickRange));
        const vecToTarget = this.targetPosition.sub(this.transform.position).normalized;
        this.acceleration = vecToTarget.scale(this.accelerationForce);
    }

    Damage(amt: number, options?: { bypassExp?: boolean; attacker?: SPBogey | SPPlayer; }): void {
        super.Damage(amt, options);

        if (options?.attacker) {
            this.target = options.attacker;
        }
    }

    Update(): void {
        const player = this.getPlayer();
        
        if (!player || ![
            SPGameState.InGame,
            SPGameState.GameOver
        ].includes(SPGameManager.Singleton.state)) return;

        if (!this.isFriendly && !this.target) {

            const validTargets: (SPPlayer|SPBogey)[] = this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey).filter(
                v => v.gameObject.id !== this.gameObject.id && (v instanceof SmartBogey ? v.isFriendly : false)
            );

            validTargets.push(SPPlayer.Singleton);

            this.target = validTargets[Random.int(0, validTargets.length)];


        }
        else if (this.isFriendly && !this.target) {
            let bogeys = this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey).filter(
                v => v.gameObject.id !== this.gameObject.id && (v instanceof SmartBogey ? !v.isFriendly : true)
            );

            bogeys = bogeys.filter(v => v instanceof HivePart ? v instanceof HiveEgg : true);

            this.target = bogeys[Random.int(0, bogeys.length)];
        }

        switch (this._state) {
            case State.AvoidingTarget:
                this.AvoidingTargetUpdate();
                break;
            case State.HeadingToLocation:
                this.HeadingToLocationUpdate();
                break;
            case State.DecidingLocation:
                this.DecidingLocationUpdate();
                break;
            case State.Wander:
                this.WanderUpdate();
                break;
        
            default:
                break;
        }

        if (this.fireRateTimer > 0) {
            this.fireRateTimer -= Time.deltaTime;

            if (this.fireRateTimer <= 0) this.fireRateTimer = 0;
        }

        this.gun.Update();

        //get sucked into gravity wells
        if (!this.isFriendly) {
            for (const well of this.engine.GetGameObjectsBehaviors<GravitonPulseBehavior>(GravitonPulseBehavior)) {
                const vec = well.transform.position.sub(this.transform.position);
                const dist = vec.magnitude;
                vec.normalize();
                if (dist <= this.size + well.size) {
                    this.velocity = this.velocity.add(vec.scale(100 * Time.deltaTime));
                }
            }
        }

        //avoid other bogeys
        for (const bogey of this.engine.GetGameObjectsBehaviors<SPBogey>(SPBogey)) {
            if (bogey.id === this.id) continue;

            const dist = Vector2.distance(this.transform.position, bogey.transform.position);

            if (dist < 48) {
                const vec = this.transform.position.sub(bogey.transform.position).normalized;
                this.acceleration = vec.scale(this.accelerationForce*2);
                break;
            }
        }

        this.AvoidBullets();

        if (this.target) {
            if (!this.target.isDead) {
                //rotate toward this.target & shoot
                let targetRot = this.target.transform.position.sub(this.transform.position).normalized.angle;
                let myRot = this.transform.rotation.angle;
    
                if (targetRot < 0) targetRot += 2 * Math.PI;
                if (myRot < 0) myRot += 2 * Math.PI;
                
                const diff = Math.abs(targetRot - myRot);
                const lerpValue = lerp(myRot, targetRot, (this.isFriendly ? 2 : 1) * Math.PI * Time.deltaTime);
    
                this.transform.rotation = Vector2.fromRadians(lerpValue);
    
                if (this.distToTarget <= this.maxShootRange && diff < 0.08725) {
                    // this.ShootAt(SPPlayer.Singleton.transform);
                    this.gun.Shoot();
                }
            } else {
                if (this._state !== State.Wander && !this.isFriendly) {
                    this.SetState(State.Wander);
                    return;
                } else if (this.isFriendly) {
                    this.target = undefined;
                }
            }
        } else if (this.isFriendly) {
            //no target. wander around player
            if (!this.wanderTarget || Vector2.distance(this.transform.position, this.wanderTarget) <= 16) this.wanderTarget = SPPlayer.Singleton.transform.position.add(Vector2.fromAngleDegrees(Random.float(0, 360)).scale(Random.float(128, 256)));

            if (this.wanderTarget) {
                this.acceleration = this.wanderTarget.sub(this.transform.position).normalized.scale(64);
                // this.transform.rotation = Vector2.lerp(this.transform.rotation, this.velocity.normalized, 4*Time.deltaTime);
                this.RotateTowardsTarget(this.velocity, Math.PI);
            }
        }

        

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(this.friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    Draw(): void {
        this.trail?.Draw();

        const p1 = this.transform.position.add(
            this.transform.forward.scale(this.size)
        );
        const p2 = this.transform.position.add(
            this.transform.right.rotate(Math.PI / 4).scale(this.size)
        );
        const p3 = this.transform.position.add(
            this.transform.back.scale(this.size / 2)
        );
        const p4 = this.transform.position.add(
            this.transform.left.rotate(-Math.PI / 4).scale(this.size)
        );
        this.ctx.fillStyle = "black";
        this.ctx.strokeStyle = this.color.toHex();
        this.ctx.lineWidth = 1;
        this.engine.strokeShape(p1, p2, p3, p4);
        this.engine.fillShape(p1, p2, p3, p4);

        this.gun.Draw(8);

        if (this.isFriendly) {
            const allFriendlies = this.engine.GetGameObjectsBehaviors<SmartBogey>(SmartBogey).filter(v => v.isFriendly);
            const yOffset = allFriendlies.map(v => v.gameObject.id).indexOf(this.gameObject.id) * 24;

            const drawPos = new Vector2(8, this.engine.height - 56 - yOffset);
            this.ctx.fillStyle = '#55ff5555';
            this.ctx.fillRect(drawPos.x, drawPos.y, 200, 8);
            this.ctx.fillStyle = '#55ff55';
            this.ctx.fillRect(drawPos.x, drawPos.y, 200 * clamp01(this.hp/this.maxHP), 8);

            this.engine.drawTexts([this.name], drawPos, {
                baseline: 'bottom'
            });
            
            this.engine.drawUIMarker(this.transform.position, this.color.toHex(), SPPlayer.Singleton.velocity.magnitude);
        }
        else if (this.isBoss) this.DrawBossBar();
    }

}